import { useLeftNavigationTheme } from '@ben/orion/hooks/use-left-navigation-theme';
import type { ITheme } from '@ben/orion/utils/theme';
import type { ActionIconProps, ElementProps } from '@mantine/core';
import { ActionIcon } from '@mantine/core';
import { forwardRef } from 'react';
import type { FlattenSimpleInterpolation } from 'styled-components';
import styled, { css } from 'styled-components';
import type { BgColorVariant } from '../types';

interface VariantProps {
  theme: ITheme;
  $menuOpened: boolean;
}

type VariantStyles = Record<
  BgColorVariant,
  (props: VariantProps) => FlattenSimpleInterpolation
>;

interface LeftNavigationActionIconProps
  extends ActionIconProps, ElementProps<'button', keyof ActionIconProps> {
  $menuOpened: boolean;
  ['aria-label']: string;
}

export const LeftNavigationActionIcon = forwardRef(
  (props: LeftNavigationActionIconProps, ref) => {
    const { bgColorVariant } = useLeftNavigationTheme();
    return (
      <StyledLeftNavigationActionIcon
        forwardedAs={undefined}
        ref={ref}
        size={24}
        radius={4}
        $bgColorVariant={bgColorVariant}
        {...props}
      />
    );
  }
);

LeftNavigationActionIcon.displayName = 'LeftNavigationActionIcon';

const variantStyles: VariantStyles = {
  LIGHT: ({ theme, $menuOpened }) => css`
    color: ${theme.palette.neutral['90']};
    background-color: ${$menuOpened
      ? theme.palette.neutral['00']
      : 'transparent'};
    &:hover {
      background-color: ${theme.palette.neutral['00']};
      color: ${theme.palette.neutral['90']};
    }
  `,
  DARK: ({ theme, $menuOpened }) => css`
    color: ${theme.palette.neutral['00']};
    background-color: ${$menuOpened
      ? theme.palette.white(0.15)
      : 'transparent'};
    &:hover {
      background-color: ${theme.palette.white(0.15)};
      color: ${theme.palette.neutral['00']};
    }
  `,
};

export const StyledLeftNavigationActionIcon = styled(ActionIcon)`
  ${({
    theme,
    $menuOpened,
    $bgColorVariant,
  }: {
    theme: ITheme;
    $menuOpened: boolean;
    $bgColorVariant: BgColorVariant;
  }) => css`
    ${variantStyles[$bgColorVariant]?.({ theme, $menuOpened })};
  `}
`;
